import styles from "./ContactEditor.module.scss";
import React, { useEffect, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ColorBox from "../../common/ColorBox";
import Editor from "../../common/Editor";
import { MODE_CREATE, MODE_EDIT } from "../../codes";
import { useMutation } from "@tanstack/react-query";
import { apiCreateContact, apiUpdateContact } from "../../api/ApiContacts";
import useToast from "../../util/useToast";
import clsx from "clsx";
import Button from "../../common/Button";
import { ContactErrors } from "../../errors/ErrorsMapping";

const ContactEditor = ({show, mode, hide, onSubmit, contact = {}}) => {
  const {showToastSuccess} = useToast();

  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    color: "#555555|#FFFFFF",
    addresses: [],
    emails: [],
    phones: []
  });

  const nameRef = useRef(null);

  const createMutation = useMutation(apiCreateContact());
  const editMutation = useMutation(apiUpdateContact(contact.id));

  useEffect(() => {
    if (show) {
      setFormErrors({});

      nameRef.current.focus();

      const addresses = contact.addresses || [];
      const phones = contact.phones || [];
      const emails = contact.emails || [];

      setFormData({
        name: contact.name || "",
        color: contact.color || "#555555|#FFFFFF",
        addresses: addresses.map(address => ({
          id: address.id,
          label: address.label,
          address: address.address
        })),
        phones: phones.map(phone => ({
          id: phone.id,
          label: phone.label,
          phone: phone.phone
        })),
        emails: emails.map(email => ({
          id: email.id,
          label: email.label,
          email: email.email
        }))
      });
    }
  }, [show, contact]);

  const submit = () => {
    let promise;

    if (mode === MODE_EDIT) {
      promise = editMutation.mutateAsync(formData);
    } else {
      promise = createMutation.mutateAsync(formData);
    }

    return promise
      .then(() => {
        return (onSubmit ? onSubmit() : Promise.resolve()).then(() => {
          if (mode === MODE_EDIT) {
            showToastSuccess("Contact modifié");
          } else {
            showToastSuccess("Contact ajouté");
          }
        });
      })
      .then(hide)
      .catch((err) => {
        if (err.response?.status === 400) {
          setFormErrors(err.response.data.errors);
        }
      });
  };

  const updateFormData = (field, context = null) => e => {
    setFormData(prev => {
      switch (field) {
        case "address_label":
          prev.addresses[context].label = e.target.value;
          break;
        case "address_address":
          prev.addresses[context].address = e.target.value;
          break;
        case "email_label":
          prev.emails[context].label = e.target.value;
          break;
        case "email_email":
          prev.emails[context].email = e.target.value;
          break;
        case "phone_label":
          prev.phones[context].label = e.target.value;
          break;
        case "phone_phone":
          prev.phones[context].phone = e.target.value;
          break;
        case "color":
          prev[field] = e;
          break;
        default:
          prev[field] = e.target.value;
          break;
      }

      return {...prev};
    });
  };

  const addAddress = () => {
    setFormData(prev => ({...prev, addresses: [...prev.addresses, {label: "", address: ""}]}));
  };

  const addPhone = () => {
    setFormData(prev => ({...prev, phones: [...prev.phones, {label: "", phone: ""}]}));
  };

  const addEmail = () => {
    setFormData(prev => ({...prev, emails: [...prev.emails, {label: "", email: ""}]}));
  };

  const removeAddress = index => () => {
    setFormData(prev => {
      return ({...prev, addresses: [...prev.addresses.slice(0, index), ...prev.addresses.slice(index + 1)]});
    });
  };

  const removePhone = index => () => {
    setFormData(prev => {
      return ({...prev, phones: [...prev.phones.slice(0, index), ...prev.phones.slice(index + 1)]});
    });
  };

  const removeEmail = index => () => {
    setFormData(prev => {
      return ({...prev, emails: [...prev.emails.slice(0, index), ...prev.emails.slice(index + 1)]});
    });
  };

  return <Editor
    show={show}
    title={mode === MODE_CREATE ? "Ajouter un contact" : "Modifier un contact"}
    onSubmit={submit}
    onCancel={hide}
    submitLabel="Sauvegarder"
    cancelLabel="Annuler"
  >
    <Form.Group className="mb-2" controlId="contact-editor-name">
      <Row>
        <Form.Label column sm={3}>Nom</Form.Label>
        <Col sm={9}>
          <Form.Control
            type="text"
            value={formData.name}
            onChange={updateFormData("name")}
            maxLength="255"
            ref={nameRef}
            isInvalid={!!formErrors.name}
          />

          <Form.Control.Feedback type="invalid">{ContactErrors.name[formErrors.name]}</Form.Control.Feedback>
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="contact-editor-color">
      <Row>
        <Form.Label column sm={3}>Couleur</Form.Label>
        <Col sm={9}>
          <ColorBox value={formData.color} onChange={updateFormData("color")}/>
        </Col>
      </Row>
    </Form.Group>

    <Row className={clsx(styles.contactCardsTitle)}>
      <Col sm={3}>&nbsp;</Col>
      <Col sm={9} className={styles.title}>
        <h5>Adresse(s)</h5>

        <Button variant="light" onClick={addAddress} size="sm">
          <span className="material-icons">add</span>
        </Button>
      </Col>
    </Row>

    {formData.addresses.map((address, index) => {
      const addressError = formErrors["addresses[" + index + "].address"];

      return <div key={index} className={styles.contactCard}>
        <div className={styles.fields}>
          <Form.Group className="mb-2" controlId={"contact-editor-address-address-" + index}>
            <Row>
              <Form.Label column sm={3}>Adresse</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={address.address}
                  onChange={updateFormData("address_address", index)}
                  maxLength="512"
                  isInvalid={!!addressError}
                />

                <Form.Control.Feedback type="invalid">{ContactErrors.phone[addressError]}</Form.Control.Feedback>
              </Col>
            </Row>
          </Form.Group>

          <Form.Group className="mb-0" controlId={"contact-editor-address-label-" + index}>
            <Row>
              <Form.Label column sm={3}>Libellé</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={address.label}
                  onChange={updateFormData("address_label", index)}
                  maxLength="255"
                />
              </Col>
            </Row>
          </Form.Group>
        </div>

        <div className={styles.actions}>
          <Button variant="light" onClick={removeAddress(index)} size="sm">
            <span className="material-icons">delete</span>
          </Button>
        </div>
      </div>;
    })}

    <Row className={clsx(styles.contactCardsTitle)}>
      <Col sm={3}>&nbsp;</Col>
      <Col sm={9} className={styles.title}>
        <h5>Téléphone(s)</h5>

        <Button variant="light" onClick={addPhone} size="sm">
          <span className="material-icons">add</span>
        </Button>
      </Col>
    </Row>

    {formData.phones.map((phone, index) => {
      const phoneError = formErrors["phones[" + index + "].phone"];

      return <div key={index} className={styles.contactCard}>
        <div className={styles.fields}>
          <Form.Group className="mb-2" controlId={"contact-editor-phone-phone-" + index}>
            <Row>
              <Form.Label column sm={3}>Téléphone</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={phone.phone}
                  onChange={updateFormData("phone_phone", index)}
                  maxLength="255"
                  isInvalid={!!phoneError}
                />

                <Form.Control.Feedback type="invalid">{ContactErrors.phone[phoneError]}</Form.Control.Feedback>
              </Col>
            </Row>
          </Form.Group>

          <Form.Group className="mb-0" controlId={"contact-editor-phone-label-" + index}>
            <Row>
              <Form.Label column sm={3}>Libellé</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={phone.label}
                  onChange={updateFormData("phone_label", index)}
                  maxLength="255"
                />
              </Col>
            </Row>
          </Form.Group>
        </div>

        <div className={styles.actions}>
          <Button variant="light" onClick={removePhone(index)} size="sm">
            <span className="material-icons">delete</span>
          </Button>
        </div>
      </div>;
    })}

    <Row className={clsx(styles.contactCardsTitle)}>
      <Col sm={3}>&nbsp;</Col>
      <Col sm={9} className={styles.title}>
        <h5>Emails(s)</h5>

        <Button variant="light" onClick={addEmail} size="sm">
          <span className="material-icons">add</span>
        </Button>
      </Col>
    </Row>

    {formData.emails.map((email, index) => {
      const emailError = formErrors["emails[" + index + "].email"];

      return <div key={index} className={styles.contactCard}>
        <div className={styles.fields}>
          <Form.Group className="mb-2" controlId={"contact-editor-email-email-" + index}>
            <Row>
              <Form.Label column sm={3}>Email</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={email.email}
                  onChange={updateFormData("email_email", index)}
                  maxLength="255"
                  isInvalid={!!emailError}
                />

                <Form.Control.Feedback type="invalid">{ContactErrors.email[emailError]}</Form.Control.Feedback>
              </Col>
            </Row>
          </Form.Group>

          <Form.Group className="mb-0" controlId={"contact-editor-email-label-" + index}>
            <Row>
              <Form.Label column sm={3}>Libellé</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={email.label}
                  onChange={updateFormData("email_label", index)}
                  maxLength="255"
                />
              </Col>
            </Row>
          </Form.Group>
        </div>

        <div className={styles.actions}>
          <Button variant="light" onClick={removeEmail(index)} size="sm">
            <span className="material-icons">delete</span>
          </Button>
        </div>
      </div>;
    })}
  </Editor>;
};

export default ContactEditor;